import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";

import {
  SPEC_TYPES,
  SPEC_TYPE_LABEL,
  specListSearchSchema,
  type SpecType,
} from "@/api/contracts/specifications";
import { SpecListPage } from "@/components/specifications/spec-list-page";
import { requireSpecsAccess } from "@/lib/route-guards";
import { specListFiltersNavigation, specListSortNavigation } from "@/lib/spec-list-url-state";

// The Spec list: one route for the three tabs, as the source's
// `specifications/:specType` is (`App.tsx:86-90`). The param changing is a tab
// change, and the page stays mounted across it — which is what carries the
// search text, Show and the page size from tab to tab while `?sort=` and
// `?filters=`, absent from the tab links, reset.
//
// The `_authed` layout has already guaranteed a session; this gate narrows it
// to one that may browse specifications. A door, not the enforcement — the
// server functions check the same capability.
//
// **An unknown `:specType` is Page not found** — a named parity break (#691
// D2, owner-ruled 2026-09-29). The source renders half a page for it: no tab
// selected, a title from the raw word, the base columns, and a list request the
// server rejects.

function isSpecType(value: string): value is SpecType {
  return (SPEC_TYPES as readonly string[]).includes(value);
}

export const Route = createFileRoute("/_authed/_chrome/specifications/$specType")({
  // `?sort=` and `?filters=` in the source's raw encodings, and nothing else;
  // anything unreadable drops to absent rather than failing the navigation.
  validateSearch: specListSearchSchema,
  beforeLoad: ({ context, params }) => {
    requireSpecsAccess(context.currentUser);
    if (!isSpecType(params.specType)) throw notFound();
  },
  // The source's `"<Type> Specifications List"`, without its product suffix
  // (`spec-list-page.tsx:29-30`).
  head: ({ params }) => ({
    meta: [
      {
        title: isSpecType(params.specType)
          ? `${SPEC_TYPE_LABEL[params.specType]} Specifications List`
          : "Page not found",
      },
    ],
  }),
  component: SpecListRoute,
});

function SpecListRoute() {
  const { specType } = Route.useParams();
  const { sort, filters } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  // `beforeLoad` has already turned anything else away.
  if (!isSpecType(specType)) throw notFound();

  return (
    <SpecListPage
      specType={specType}
      rawSort={sort}
      // A replace, other params kept, the explicit `?sort=` written as itself
      // — the rule is `specListSortNavigation`'s, so the routing test runs it.
      onSortChange={(next) => {
        void navigate(specListSortNavigation(next));
      }}
      rawFilters={filters}
      // A push, other params kept, an empty list deleting the param — the
      // rule is `specListFiltersNavigation`'s, so the routing test runs it.
      onFiltersChange={(next) => {
        void navigate(specListFiltersNavigation(next));
      }}
    />
  );
}
